<template>
  <div :class="{
    'choices': true,
    'choices--show-select': showSelect,
  }">
    <div :class="wrapperClasses" v-if="!useSelect || !showSelect">
      <label
        v-for="item in filteredItems"
        :key="item.value"
        :class="itemClasses(item)"
      >
        <input
          type="radio"
          :name="name"
          class="radios__item-radio"
          :value="item.value"
          v-model="value"
          @change="selectionChanged"
        />
        <component
          v-if="item.icon"
          :is="item.icon"
          :extra-classes="itemIconClasses(item)"
        />
        <span class="long" v-if="item.title">
          {{ item.title }}
        </span>
        <span class="short" v-if="item.shortTitle">
          {{ item.shortTitle }}
        </span>
      </label>
    </div>
    <div class="choices__edit-select" v-if="showSelect">
      <select name="select" v-model="value" @change="selectionChanged">
        <option
          v-for="item in filteredItems"
          :key="item.title"
          :value="item.value"
        >
          {{ item.title }}
        </option>
      </select>
    </div>
    <a
      v-if="showEditButton"
      :class="{
        'choices__edit': true,
        'choices__edit--active': showSelect,
      }"
      @click="showSelect = !showSelect"
      title="ändern"
    >
      <IconEdit />
    </a>
  </div>
</template>

<script setup>
  import Caret from '@/components/icons/Caret.vue'
  import CircleEmpty from '@/components/icons/CircleEmpty.vue'
  import Check from '@/components/icons/Check.vue'
  import IconEdit from '@/components/icons/Edit.vue'
  import { computed, ref } from 'vue'

  /**
   * This component is supposed to be used as replacement for radio buttons to
   * show a horizontal list of choices and allow selecting one option at a time.
   *
   * In 2022 it was extended to show only the selected value and a toggle button,
   * which replaces the selected value with a <select>.
   *
   * This component is a slightly cleaned up vue3 migration of these features.
   */

  const emit = defineEmits(['selectionChanged', 'updateEdit'])
  const props = defineProps({
    name: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    default: {
      type: String,
    },
    extraClasses: {
      type: Array,
      default: () => [],
    },
    useSelect: {
      type: Boolean,
      default: false,
    },
    showEditButton: {
      /**
       * Whether or not the edit button will be shown, when useSelect === true.
       * This is used to prevent the user from editing connection priorities
       * when the connection is locked.
       */
      type: Boolean,
      default: true,
    }
  })

  const value = ref(props.default)

  // Only used when useSelect === true:
  const showSelect = ref(false)

  const showEditButton = computed(() => {
    return props.useSelect && props.showEditButton
  })

  const wrapperClasses = computed(() => {
    return ['radios'].concat(props.extraClasses).join(' ')
  })

  const filteredItems = computed(() => {
    if (props.useSelect && !showSelect.value) {
      return props.items.filter(item => item.value === value.value)
    }
    return props.items
  })

  const itemClasses = (item) => {
    let classes = ['radios__item']
    if (item.value === value.value) {
      classes.push(`radios__item--active-${item.value}`)
    }
    if (item.shortTitle !== undefined) {
      classes.push(`radios__item--shortable`)
    }
    return classes.join(' ')
  }

  const itemIconClasses = (item) => {
    let classes = ['radios__item-icon']

    switch (item.value) {
      case 'high':
        classes.push('icon--caret--up')
        break

      case 'low':
        classes.push('icon--caret--down')
        break
    }

    return classes
  }

  const selectionChanged = () => {
    emit('selectionChanged', value.value)
  }

  defineExpose({
    showSelect,
    value,
  })
</script>

<style lang="scss">
.choices {
  display: inline-flex;

  &__edit {
    cursor: pointer;
    transition: $transition-base;
    padding: 0 $spacer025 $spacer05;
    border-radius: $border-radius;
    margin-left: $spacer05;

    @media (min-width: $bp-md) {
      margin-left: $spacer05;
      align-self: flex-start;
    }

    &--active {
      background: $grey-dark;
      color: $white;
    }

    @include hover-focus-active {
      background: $grey-dark;
      color: $white;
    }

    .icon {
      width: 1em;
      vertical-align: middle;
      // Vertical visual alignment.
      margin-top: $spacer01 * -1;
    }
    &-select select {
      background: $white;
      color: $grey-darker;
      height: 32px;
      border-radius: $border-radius;
    }
  }
}

.radios {
  $color: $grey-lighter;
  border-radius: $border-radius;
  display: inline-flex;

  &__item {
    padding: $spacer01 $spacer05;
    border: 1px solid $color;
    border-right: none;
    position: relative;
    cursor: pointer;
    transition: $transition-base;
    font-size: $font-size-sm;
    align-self: flex-start;

    @media (min-width: $bp-xs) {
      font-size: $font-size-base;
      padding: $spacer01 $spacer075;
    }

    &:first-child {
      border-radius: $border-radius 0 0 $border-radius;
    }

    &:last-child {
      border-right: 1px solid $color;
      border-radius: 0 $border-radius $border-radius 0;
    }

    &:only-child {
      //abandoned by China since 2015
      border-right: 1px solid $color;
      border-radius: $border-radius;
      cursor: not-allowed;
    }

    &--shortable .short {
      display: none;

      @media (max-width: $bp-md) {
        display: block;
      }
    }
    &--shortable .long {
      display: block;

      @media (max-width: $bp-md) {
        display: none;
      }
    }

    &--inactive {
      display: none;
    }

    &--active {
      background: $color;
      color: $white;

      &-veryhigh {
        background: $red;
        border-color: $red;
        color: $white;

        &:last-child {
          border-right-color: transparent;
        }

        + .radios__item {
          border-left-color: transparent;
        }
      }
      &-high {
        background: $green-dark;
        border-color: $green-dark;
        color: $white;

        &:last-child {
          border-right-color: transparent;
        }

        + .radios__item {
          border-left-color: transparent;
        }
      }

      &-default {
        border-color: $community;
        background: $community;
        color: $grey-darker;

        &:last-child {
          border-right-color: transparent;
        }

        + .radios__item {
          border-left-color: transparent;
        }
      }

      &-low {
        border-color: $grey-dark;
        background: $grey-dark;
        color: $white;

        &:last-child {
          border-right-color: transparent;
        }

        + .radios__item {
          border-left-color: transparent;
        }
      }
      &-verylow {
        border-color: $grey-mediumdark;
        background: $grey-mediumdark;
        color: $white;

        &:last-child {
          border-right-color: transparent;
        }

        + .radios__item {
          border-left-color: transparent;
        }
      }
      &-EG {
        border-color: $grey-darker;
        background: $grey-darker;
        color: $white;

        &:last-child {
          border-right-color: transparent;
        }

        + .radios__item {
          border-left-color: transparent;
        }
      }
    }

    &-radio {
      position: absolute;
      opacity: 0;
      z-index: -1;
    }

    &-icon {
      max-width: 0.8em;
      vertical-align: middle;
    }
  }
}
//  .icon {
//    width: 1em;
//    vertical-align: middle;
//    // Vertical visual alignment.
//    margin-top: $spacer01 * -1;
//    margin-left: $spacer05;
//  }
</style>